import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import CommonModal from "../common/CommonModal";
import { useAuth } from "contexts/AuthContext";
import { useApplicationForm } from "hooks/useApplicationForm";
import { useTranslation } from "react-i18next";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTemplate } from "hooks/useTemplate";
import { useClasses } from "hooks/useClasses";
import { useError } from "contexts/ErrorContext";

interface Props {
  open: boolean;
  handleClose: () => void;
  schoolId?: number;
}

const UploadBulkApplicationsModal: React.FC<Props> = ({
  open,
  handleClose,
  schoolId,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorFile, setErrorFile] = useState<Blob | null>(null);
  const { user } = useAuth();
  const { uploadBulkApplicationsMutation } = useApplicationForm(false, schoolId);
  const { selectedSession, selectedClass, setSelectedClass } = useSessionClass();
  const { classesQuery } = useClasses(schoolId);
  const { t } = useTranslation();
  const { downloadBulkApplicationTemplate } = useTemplate();
  const { showError, showSuccess } = useError();

  React.useEffect(() => {
    if (open) {
      setError(null);
      setSelectedFile(null);
      setErrorFile(null);
      
      // Validate that the selected class still exists
      if (selectedClass?.id && classesQuery.data) {
        const classExists = classesQuery.data.find((cls) => cls.id === selectedClass.id);
        if (!classExists) {
      // Clear the stale class selection
      setSelectedClass(null);
      const errorMsg = t("selected_class_no_longer_exists");
      setError(errorMsg);
      showError(errorMsg);
        }
      }
    }
  }, [open, selectedClass, classesQuery.data, setSelectedClass, t, showError]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setErrorFile(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      // Check if file is .xlsx format
      if (fileExtension !== '.xlsx') {
        const errorMsg = t("please_check_file_format_only_xlsx_accepted") || "Please check the format of file only .xlsx files accepted";
        setError(errorMsg);
        showError(errorMsg);
        setSelectedFile(null);
        // Reset the input
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadBulkApplicationTemplate({
        selectedClass,
        selectedSession,
      });
    } catch (error) {
      const errorMsg = t("failed_to_download_template");
      setError(errorMsg);
      showError(errorMsg);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedClass || !selectedSession) {
      const errorMsg = t("please_select_file_class_session");
      setError(errorMsg);
      return;
    }

    // Add type guards to ensure IDs are defined
    if (!selectedClass.id || !selectedSession.id) {
      const errorMsg = t("class_or_session_id_missing");
      setError(errorMsg);
      return;
    }

    // Validate that the selected class still exists in the database
    const classExists = classesQuery.data?.find((cls) => cls.id === selectedClass.id);
    if (!classExists) {
      const errorMsg = t("selected_class_no_longer_exists");
      setError(errorMsg);
      return;
    }

    setUploading(true);
    setError(null);
    setErrorFile(null);

    try {
      const result = await uploadBulkApplicationsMutation.mutateAsync({
        file: selectedFile,
        schoolId: selectedClass.schoolId,
        classId: selectedClass.id,
        sessionId: selectedSession.id,
      });

      if (result.success) {
        showSuccess(t("bulk_upload_successful"));
        handleClose();
      } else {
        const errorMsg = result.message || t("bulk_upload_failed");
        setError(errorMsg);
        
        // Set error file for manual download
        if (result.errorFile) {
          setErrorFile(result.errorFile);
        }
      }
    } catch (error: any) {
      // Check if error contains an Excel file
      if (error.response?.data instanceof Blob) {
        const contentType = error.response?.headers['content-type'];
        const isExcelError = contentType && (
          contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
          contentType.includes('application/octet-stream') ||
          contentType.includes('application/excel')
        );
        
        if (isExcelError) {
          const errorFile = error.response.data;
          setErrorFile(errorFile);
          const errorMsg = t("bulk_upload_failed") + ". " + t("application_form_errors_detected_download_file");
          setError(errorMsg);
        } else {
          // Try to extract error message from blob
          try {
            const text = await error.response.data.text();
            const errorMessage = text || error.message || t("upload_failed");
            setError(errorMessage);
          } catch {
            const errorMessage = error.message || t("upload_failed");
            setError(errorMessage);
          }
        }
      } else {
        const errorMessage = error.response?.data?.message || error.message || t("upload_failed");
        setError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadErrorFile = () => {
    if (errorFile) {
      try {
        const url = window.URL.createObjectURL(errorFile);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'application_form_errors.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url); // Clean up
        // Close modal after downloading error file
        handleClose();
      } catch (err) {
        setError(t("error_downloading_file"));
      }
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={t("upload_bulk_applications")}
      maxWidth="md"
      customActions={
        <>
          <Button onClick={handleClose} disabled={uploading} variant="outlined" sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !selectedFile}
            startIcon={uploading && <CircularProgress size={20} />}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {uploading ? t("uploading") : t("upload_excel_file")}
          </Button>
        </>
      }
    >
      <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Class Selection - Readonly */}
        <TextField
          label="Class"
          value={selectedClass ? `${selectedClass.name} - ${selectedClass.section}` : t("no_class_selected")}
          InputProps={{ readOnly: true }}
          fullWidth
          size="small"
          variant="outlined"
        />

        {/* Session Display - Readonly */}
        <TextField
          label="Session"
          value={selectedSession?.name || t("no_session_selected")}
          InputProps={{ readOnly: true }}
          fullWidth
          size="small"
          variant="outlined"
        />

        {/* Download Template Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownloadTemplate}
            startIcon={<DownloadIcon />}
            disabled={!selectedClass || !selectedSession}
          >
            Download Template
          </Button>
        </Box>

        {/* Template Instructions */}
        <Box sx={{
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #ddd',
          fontSize: '0.875rem'
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Template Instructions:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Gender:</strong> {t("gender_instruction")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Subjects:</strong> Enter subject codes separated by commas (e.g., "401,411,512")
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Dates:</strong> Use DD/MM/YYYY format
          </Typography>
          <Typography variant="body2">
            • <strong>Required Fields:</strong> All fields marked with * are mandatory
          </Typography>
        </Box>

        {/* File Upload */}
        <input
          accept=".xlsx"
          type="file"
          onChange={handleFileChange}
          onClick={() => {
            // Clear error message and error file when file input is clicked again
            setError(null);
            setErrorFile(null);
          }}
          style={{ marginBottom: 16 }}
        />

        {error && (
          <Box sx={{ mt: 2 }}>
            <Typography
              color="error"
              variant="body2"
              sx={{
                backgroundColor: "#fff3f3",
                padding: 2,
                borderRadius: 1,
                border: "1px solid #ffcdd2",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {errorFile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadErrorFile}
              startIcon={<DownloadIcon />}
            >
              {t("download_error_file")}
            </Button>
          </Box>
        )}
      </Box>
    </CommonModal>
  );
};

export default UploadBulkApplicationsModal;
